import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';
import { ListParams } from 'src/utils/useListSearchParams';

import { Group } from '../group/_types';

import {
  AssignedResource,
  GroupResource,
  Resource,
  ThumbnailAsset,
} from './_types';
import {
  denormalizeGroupResource,
  denormalizeResource,
  normalizeAssignedResource,
  normalizeGroupResource,
  normalizeResource,
} from './normalizers';

interface FetchAssignedResourceOptions {
  userId?: Identifier;
  tags?: string[];
}

export const fetchAssignedResource = async ({
  userId,
  tags,
}: FetchAssignedResourceOptions): Promise<AssignedResource[]> => {
  const response = await api.get(`/api/v3/assigned_resources/`, {
    params: {
      fetch_all: true,
      user_id: userId,
      ...(tags?.length && { 'q[resource_tags_name_eq_any]': tags }),
    },
  });
  return response.data.map(normalizeAssignedResource);
};

export const fetchMyAssignedResource = async ({
  page = 1,
  perPage = 20,
  search = '',
  sortBy = 'createdAt',
  sortDir = 'desc',
  filters = {},
}: ListParams & {
  filters: { tags?: string[]; matchAllTags?: boolean };
}): Promise<{ data: AssignedResource[]; total: number }> => {
  const response = await api.get(`/api/v3/assigned_resources/`, {
    params: {
      page,
      per: perPage,
      'q[resource_caption_cont]': search,
      'q[s]': `${decamelize(sortBy)} ${sortDir}`,
      ...(filters.tags && { 'q[resource_tags_name_eq_any]': filters.tags }),
    },
  });
  return {
    data: response.data.assigned_resource.map(normalizeAssignedResource),
    total: response.data.meta.total,
  };
};

interface DeleteAssignedResourceOptions {
  id: Identifier;
}

export const deleteAssignedResource = async ({
  id,
}: DeleteAssignedResourceOptions): Promise<AssignedResource> => {
  const response = await api.delete(`/api/v3/assigned_resources/${id}`);
  return normalizeAssignedResource(response.data);
};

interface FetchResourcesOptions {
  fetchAll?: boolean;
  pluckColumns?: string;
  sort?: string;
}

export const fetchResourceById = async (
  id: Identifier,
  { forAppWebview = false }: { forAppWebview?: boolean } = {}
): Promise<Resource> => {
  const response = await api.get(`/api/v3/resources/${id}`, {
    params: {
      ...(forAppWebview && { for_app_webview: true }),
    },
  });
  return normalizeResource(response.data);
};

export const fetchResources = async ({
  fetchAll = false,
  pluckColumns,
  sort,
}: FetchResourcesOptions = {}): Promise<Resource[]> => {
  const response = await api.get('/api/v3/resources', {
    params: {
      fetch_all: fetchAll,
      ...(pluckColumns && { pluck_columns: pluckColumns }),
      ...(sort && { q: { s: sort } }),
    },
  });

  return response.data.map(normalizeResource);
};

export const fetchResourcesByParams = async ({
  page = 1,
  perPage = 20,
  search = '',
  sortBy,
  sortDir,
  tags,
}): Promise<{
  data: Resource[];
  total: number;
}> => {
  const response = await api.get('/api/v3/resources', {
    params: {
      page,
      per: perPage,
      'q[caption_cont]': search,
      'q[tags_name_eq_any]': tags,
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
      }),
    },
  });

  return {
    data: response.data.resource.map(normalizeResource),
    total: response.data.meta.total,
  };
};

interface BulkUpdateResourcesOptions {
  ids: Identifier[];
  groupIds: Identifier[];
  tags: string[];
  delayInterval: string;
  expiresAt: string;
}
export const bulkUpdateResources = async ({
  ids,
  groupIds,
  tags,
  delayInterval,
  expiresAt,
}: BulkUpdateResourcesOptions): Promise<{ success: string }> => {
  const response = await api.get(`/api/v3/resources/bulk_update`, {
    params: {
      resource_ids: ids,
      group_ids: groupIds,
      tags,
      delay_interval: delayInterval,
      expires_at: expiresAt,
    },
  });
  return response.data;
};

export const deleteResource = async (id: Resource['id']): Promise<Resource> => {
  const response = await api.delete(`/api/v3/resources/${id}`);
  return normalizeResource(response.data);
};

interface FetchGroupResourcesOptions {
  fetchAll?: boolean;
  forUser?: boolean;
  filters?: {
    tags?: string[];
    groupId?: Identifier;
  };
  sortBy?: string;
  sortDir?: string;
  page?: number;
  perPage?: number;
  search?: string;
}

export const fetchGroupResources = async ({
  page = 1,
  perPage = 20,
  search = '',
  sortBy,
  sortDir,
  fetchAll = false,
  forUser = false,
  filters = {},
}: FetchGroupResourcesOptions): Promise<{
  data: GroupResource[];
  total: number;
}> => {
  const response = await api.get('/api/v3/group_resources', {
    params: {
      page,
      per: perPage,
      fetch_all: fetchAll,
      for_user: forUser,
      'q[name_cont]': search,
      'q[s]':
        sortBy && sortDir ? `${decamelize(sortBy)} ${sortDir}` : undefined,
      ...(filters.tags && { 'q[tags_name_eq_any]': filters.tags }),
      ...(filters.groupId && { 'q[group_id_eq]': filters.groupId }),
    },
  });

  return {
    data: response.data.group_resource.map(normalizeGroupResource),
    total: response.data.meta.total,
  };
};

export const fetchGroupResourcesByGroupId = async ({
  groupId,
  search,
  tags,
  sortBy,
  sortDir,
  page,
  perPage,
}: {
  groupId: Group['id'];
  search?: string;
  tags?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  perPage?: number;
}): Promise<{ groupResources: Resource[]; total: number }> => {
  const response = await api.get(`/api/v3/group_resources`, {
    params: {
      group_id: groupId,
      'q[any_tags]': tags,
      'q[name_cont]': search,
      'q[s]': sortBy && sortDir && `${sortBy} ${sortDir}`,
      page,
      per: perPage,
    },
  });

  const normalizedResources = response.data?.group_resource?.map(
    (groupresource) => normalizeResource(groupresource)
  );

  return {
    groupResources: normalizedResources,
    total: response.data?.meta?.total,
  };
};

export const fetchGroupResource = async ({
  groupResourceId,
  forAppWebview = false,
}: {
  groupResourceId: GroupResource['id'];
  forAppWebview?: boolean;
}): Promise<GroupResource> => {
  const response = await api.get(`/api/v3/group_resources/${groupResourceId}`, {
    params: {
      ...(forAppWebview && { for_app_webview: true }),
    },
  });
  return normalizeGroupResource(response.data);
};

export const handleResourceFileUpload = async (
  file: File,
  size: string | null = null
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  let url = '/api/v2/assets/image/resource';
  if (size) {
    url = `/api/v2/assets/image/resource?size=${size}`;
  }
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const handleResourceUpload = async ({
  file,
  id = '',
}: {
  file: File;
  id?: Identifier;
}) => {
  const formData = new FormData();
  formData.append('resource[file]', file);
  const response = await api[id ? 'put' : 'post'](
    `/api/v3/resources/${id}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: !id && { standalone: true },
    }
  );
  return normalizeResource(response.data);
};

export const newGroupResource = async ({
  data,
}: {
  data: Partial<GroupResource>;
}) => {
  const response = await api.post(`/api/v3/group_resources`, {
    group_resource: denormalizeGroupResource(data),
  });
  return normalizeGroupResource(response.data);
};

export const updateGroupResource = async ({
  groupResourceId,
  data,
}: {
  groupResourceId: GroupResource['id'];
  data: Partial<GroupResource>;
}) => {
  const response = await api.put(`/api/v3/group_resources/${groupResourceId}`, {
    group_resource: denormalizeGroupResource(data),
  });
  return normalizeGroupResource(response.data);
};

export const deleteGroupResource = async ({
  groupResourceId,
}: {
  groupResourceId: Resource['id'];
}) => {
  await api.delete(`/api/v3/group_resources/${groupResourceId}`);
};

interface CreateAssignedResourceOptions {
  userId: Identifier;
  resourceId: Identifier;
  notifyClient: boolean;
  message?: string;
}

export const createAssignedResource = async ({
  userId,
  resourceId,
  notifyClient,
  message,
}: CreateAssignedResourceOptions): Promise<AssignedResource> => {
  const response = await api.post('/api/v3/assigned_resources', {
    user_id: userId,
    notify_client: notifyClient,
    resource_id: resourceId,
    message: message,
  });
  return normalizeAssignedResource(response.data);
};

export const createResource = async (
  file: File | null,
  resource?: Partial<Resource>
): Promise<Resource> => {
  let response;
  if (!resource) {
    resource = {};
  }
  if (file) {
    const formData = new FormData();
    formData.append('resource[file]', file);
    response = await api.post('/api/v3/resources', formData, {
      params: { resource: denormalizeResource(resource) },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } else {
    response = await api.post('/api/v3/resources', {
      resource: denormalizeResource(resource),
    });
  }

  return normalizeResource(response.data);
};

export const updateResource = async (
  resource: Partial<Resource>
): Promise<Resource> => {
  const { id, ...resourceWithoutId } = resource;
  const response = await api.put(`/api/v3/resources/${id}`, {
    resource: denormalizeResource(resourceWithoutId),
  });
  return normalizeResource(response.data);
};

export const createThumbnailResource = async (
  file: File
): Promise<ThumbnailAsset> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/resource', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data; // TODO: normalize (id and url are the only returned values)
};

export const createStandaloneResource = async (
  file: File,
  options?: {
    attacheableId?: Identifier;
    attacheableType?: string;
  }
): Promise<{ id: Identifier; fileUrl: string }> => {
  const formData = new FormData();
  formData.append('resource[file]', file);
  const response = await api.post(
    '/api/v3/resources?standalone=true',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        ...(options?.attacheableId != null
          ? {
              attacheable_id: options.attacheableId,
              attacheable_type: options.attacheableType ?? 'WorkoutPlan',
            }
          : {}),
      },
    }
  );
  const data = response.data?.resource ?? response.data;
  return {
    id: data.id,
    fileUrl: data.file_url ?? data.fileUrl,
  };
};

export const fetchAllGroupResourcesForUser = async (): Promise<
  GroupResource[]
> => {
  const response = await api.get('/api/v3/group_resources', {
    params: { fetch_all: true, for_user: true },
  });
  return response.data.map(normalizeGroupResource);
};

export const fetchFavoriteAssignedResources = async (): Promise<
  AssignedResource[]
> => {
  const response = await api.get('/api/v3/assigned_resources/', {
    params: { fetch_all: true, favorites: true },
  });
  return response.data.map(normalizeAssignedResource);
};

export const fetchFavoriteGroupResources = async (): Promise<
  GroupResource[]
> => {
  const response = await api.get('/api/v3/group_resources', {
    params: { fetch_all: true, for_user: true, favorites: true },
  });
  return response.data.map(normalizeGroupResource);
};

export const toggleResourceFavorite = async ({
  id,
  isGroup,
  favorite,
}: {
  id: Identifier;
  isGroup: boolean;
  favorite: boolean;
}): Promise<{ isFavorite: boolean }> => {
  const basePath = isGroup ? 'group_resources' : 'resources';
  const response = await api.get(`/api/v3/${basePath}/${id}/favorite`, {
    params: { favorite, time: Date.now() },
  });
  return { isFavorite: response.data.is_favorite };
};
